import { useState, useCallback, useMemo } from 'react';
import { GridColumn } from '../../../../types/datagrid';

interface UseGridColumnsParams {
  initialColumns: GridColumn[];
  onColumnChange?: (columns: GridColumn[]) => void;
}

export const useGridColumns = ({ initialColumns, onColumnChange }: UseGridColumnsParams) => {
  const [columns, setColumns] = useState<GridColumn[]>(initialColumns);

  // Handle column reordering
  const handleColumnReorder = useCallback(
    (columnId: string, targetIndex: number) => {
      setColumns(prevColumns => {
        const newColumns = [...prevColumns];
        const sourceIndex = newColumns.findIndex(col => col.id === columnId);

        if (sourceIndex === -1) return prevColumns;

        // Remove from source position
        const [movedColumn] = newColumns.splice(sourceIndex, 1);

        // Insert at target position
        newColumns.splice(targetIndex, 0, movedColumn);

        // Update order values
        const updatedColumns = newColumns.map((col, index) => ({
          ...col,
          order: index,
        }));

        onColumnChange?.(updatedColumns);
        return updatedColumns;
      });
    },
    [onColumnChange]
  );

  // Handle column resizing
  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      setColumns(prevColumns => {
        const newColumns = prevColumns.map(col => {
          if (col.id === columnId) {
            const constrainedWidth = Math.max(col.minWidth, Math.min(width, col.maxWidth || 500));
            return { ...col, width: constrainedWidth };
          }
          return col;
        });

        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback(
    (columnId: string, isVisible: boolean) => {
      setColumns(prevColumns => {
        const newColumns = prevColumns.map(col =>
          col.id === columnId ? { ...col, isVisible } : col
        );

        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Handle column rename
  const handleColumnRename = useCallback(
    (columnId: string, newLabel: string) => {
      setColumns(prevColumns => {
        const newColumns = prevColumns.map(col =>
          col.id === columnId ? { ...col, label: newLabel.trim() || col.originalLabel } : col
        );

        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Handle column pin/unpin
  const handleColumnPin = useCallback(
    (columnId: string, pinned: 'left' | 'right' | false) => {
      setColumns(prevColumns => {
        const newColumns = prevColumns.map(col =>
          col.id === columnId ? { ...col, isPinned: pinned } : col
        );

        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Auto-size column
  const handleColumnAutoSize = useCallback(
    (columnId: string, data: any[] = []) => {
      const column = columns.find(col => col.id === columnId);
      if (!column) return;

      // Calculate width based on content
      let maxWidth = column.label.length * 8; // Base width for header

      // Check data values
      data.forEach(row => {
        const value = row[column.key];
        if (value != null) {
          const stringValue = String(value);
          const contentWidth = stringValue.length * 7; // Approximate character width
          maxWidth = Math.max(maxWidth, contentWidth);
        }
      });

      // Add padding and constrain to limits
      const calculatedWidth = Math.max(
        column.minWidth,
        Math.min(maxWidth + 32, column.maxWidth || 300)
      );

      handleColumnResize(columnId, calculatedWidth);
    },
    [columns, handleColumnResize]
  );

  // Auto-size all columns
  const handleAutoSizeAll = useCallback(
    (data: any[] = []) => {
      columns.forEach(column => {
        if (column.isResizable) {
          handleColumnAutoSize(column.id, data);
        }
      });
    },
    [columns, handleColumnAutoSize]
  );

  // Reset columns to original state
  const resetColumns = useCallback(() => {
    const resetColumns = initialColumns.map(col => ({
      ...col,
      label: col.originalLabel,
      isVisible: true,
      width: undefined, // Remove custom width
    }));

    setColumns(resetColumns);
    onColumnChange?.(resetColumns);
  }, [initialColumns, onColumnChange]);

  // Add calculated column
  const addCalculatedColumn = useCallback(
    (calculatedColumn: Omit<GridColumn, 'order'>) => {
      setColumns(prevColumns => {
        const newColumn: GridColumn = {
          ...calculatedColumn,
          order: prevColumns.length,
          type: 'calculated',
        };

        const newColumns = [...prevColumns, newColumn];
        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Remove column
  const removeColumn = useCallback(
    (columnId: string) => {
      setColumns(prevColumns => {
        const newColumns = prevColumns
          .filter(col => col.id !== columnId)
          .map((col, index) => ({ ...col, order: index }));

        onColumnChange?.(newColumns);
        return newColumns;
      });
    },
    [onColumnChange]
  );

  // Computed values
  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.isVisible).sort((a, b) => a.order - b.order);
  }, [columns]);

  const pinnedLeftColumns = useMemo(() => {
    return visibleColumns.filter(col => col.isPinned === 'left');
  }, [visibleColumns]);

  const pinnedRightColumns = useMemo(() => {
    return visibleColumns.filter(col => col.isPinned === 'right');
  }, [visibleColumns]);

  const unpinnedColumns = useMemo(() => {
    return visibleColumns.filter(col => !col.isPinned);
  }, [visibleColumns]);

  const hiddenColumns = useMemo(() => {
    return columns.filter(col => !col.isVisible);
  }, [columns]);

  const resizableColumns = useMemo(() => {
    return columns.filter(col => col.isResizable);
  }, [columns]);

  const sortableColumns = useMemo(() => {
    return columns.filter(col => col.isSortable);
  }, [columns]);

  const editableColumns = useMemo(() => {
    return columns.filter(col => col.isEditable);
  }, [columns]);

  const calculatedColumns = useMemo(() => {
    return columns.filter(col => col.type === 'calculated');
  }, [columns]);

  // Column utilities
  const getColumnById = useCallback(
    (columnId: string) => {
      return columns.find(col => col.id === columnId);
    },
    [columns]
  );

  const getColumnByKey = useCallback(
    (key: string) => {
      return columns.find(col => col.key === key);
    },
    [columns]
  );

  const isColumnVisible = useCallback(
    (columnId: string) => {
      return columns.find(col => col.id === columnId)?.isVisible ?? false;
    },
    [columns]
  );

  const isColumnLocked = useCallback(
    (columnId: string) => {
      return columns.find(col => col.id === columnId)?.isLocked ?? false;
    },
    [columns]
  );

  const isColumnPrivate = useCallback(
    (columnId: string) => {
      return columns.find(col => col.id === columnId)?.isPrivate ?? false;
    },
    [columns]
  );

  const canHideColumn = useCallback(
    (columnId: string) => {
      const column = columns.find(col => col.id === columnId);
      return column ? column.isHideable && !column.isPrivate : false;
    },
    [columns]
  );

  const canRenameColumn = useCallback(
    (columnId: string) => {
      const column = columns.find(col => col.id === columnId);
      return column ? column.isRenameable : false;
    },
    [columns]
  );

  const canResizeColumn = useCallback(
    (columnId: string) => {
      const column = columns.find(col => col.id === columnId);
      return column ? column.isResizable && !column.isLocked : false;
    },
    [columns]
  );

  const canReorderColumn = useCallback(
    (columnId: string) => {
      const column = columns.find(col => col.id === columnId);
      return column ? !column.isLocked : false;
    },
    [columns]
  );

  // Get column statistics
  const getColumnStats = useCallback(() => {
    return {
      total: columns.length,
      visible: visibleColumns.length,
      hidden: hiddenColumns.length,
      pinned: pinnedLeftColumns.length + pinnedRightColumns.length,
      calculated: calculatedColumns.length,
      resizable: resizableColumns.length,
      sortable: sortableColumns.length,
      editable: editableColumns.length,
    };
  }, [
    columns.length,
    visibleColumns.length,
    hiddenColumns.length,
    pinnedLeftColumns.length,
    pinnedRightColumns.length,
    calculatedColumns.length,
    resizableColumns.length,
    sortableColumns.length,
    editableColumns.length,
  ]);

  return {
    // State
    columns,
    visibleColumns,
    pinnedLeftColumns,
    pinnedRightColumns,
    unpinnedColumns,
    hiddenColumns,
    resizableColumns,
    sortableColumns,
    editableColumns,
    calculatedColumns,

    // Actions
    handleColumnReorder,
    handleColumnResize,
    handleColumnVisibilityChange,
    handleColumnRename,
    handleColumnPin,
    handleColumnAutoSize,
    handleAutoSizeAll,
    resetColumns,
    addCalculatedColumn,
    removeColumn,

    // Utilities
    getColumnById,
    getColumnByKey,
    isColumnVisible,
    isColumnLocked,
    isColumnPrivate,
    canHideColumn,
    canRenameColumn,
    canResizeColumn,
    canReorderColumn,
    getColumnStats,

    // For external state updates
    setColumns: useCallback(
      (newColumns: GridColumn[]) => {
        setColumns(newColumns);
        onColumnChange?.(newColumns);
      },
      [onColumnChange]
    ),
  };
};
